import React, { createContext, useCallback, useContext, useMemo, ReactNode } from 'react';

interface OptimizedImageInfo {
    nodeId: string;
    altText: string;
    component: string;
    field: string;
}

interface BuilderImageContextType {
    optimizedImages: Record<string, OptimizedImageInfo>;
    getOptimizedImage: (originalUrl: string) => OptimizedImageInfo | null;
}

const BuilderImageContext = createContext<BuilderImageContextType>({
    optimizedImages: {},
    getOptimizedImage: () => null,
});

interface BuilderImageProviderProps {
    children: ReactNode;
    optimizedImages?: Record<string, OptimizedImageInfo>;
}

// Strip query params from Builder CDN URLs before lookup so that URLs the
// Builder SDK auto-transforms (e.g. appending ?format=webp, ?width=800) still
// match the keys stored at build time which are always the bare URL.
const normalizeBuilderUrl = (url: string): string => url.split('?')[0];

export const BuilderImageProvider: React.FC<BuilderImageProviderProps> = ({ children, optimizedImages = {} }) => {
    const getOptimizedImage = useCallback(
        (originalUrl: string): OptimizedImageInfo | null => {
            const normalizedUrl = normalizeBuilderUrl(originalUrl);
            return optimizedImages[normalizedUrl] || optimizedImages[originalUrl] || null;
        },
        [optimizedImages]
    );

    const contextValue = useMemo(() => ({ optimizedImages, getOptimizedImage }), [optimizedImages, getOptimizedImage]);

    return <BuilderImageContext.Provider value={contextValue}>{children}</BuilderImageContext.Provider>;
};

export const useBuilderImages = (): BuilderImageContextType => {
    const context = useContext(BuilderImageContext);
    if (!context) {
        throw new Error('useBuilderImages must be used within a BuilderImageProvider');
    }
    return context;
};
